import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Divider,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  Description as TermsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = "Terms & Conditions - adiLessons";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Read the terms and conditions of using the adiLessons driving lesson management and scheduling platform.');
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f4f6f9' }}>
      {/* Navigation Header */}
      <AppBar
        position="sticky"
        sx={{
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.05)',
          color: '#1a237e',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
          <Box display="flex" alignItems="center" gap={1} onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
            <CarIcon sx={{ color: '#1967D2', fontSize: 28 }} />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#2b3154', 
                letterSpacing: '-0.5px',
                fontFamily: '"Outfit", "Inter", sans-serif'
              }}
            >
              adi<span style={{ color: '#E53935', fontWeight: '900' }}>L</span>essons
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Back to Home
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 6, flexGrow: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: 4,
            border: '1px solid #e0e0e0',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.02)',
          }}
        >
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
            <TermsIcon color="primary" sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                Terms & Conditions
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last Updated: June 14, 2026
              </Typography>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3 }} />

          <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            Welcome to <strong>adiLessons</strong> (operated under the brand name <strong>adiLessons</strong>). Please read these Terms & Conditions carefully before using our software, application, and booking management website. By accessing or using our platform, you agree to be bound by these terms.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            1. Parties & Eligibility
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            This agreement is between the customer (registered Approved Driving Instructor (ADI), driving school, or student booking user) and adiLessons. To register an account, you must be at least 18 years of age (or have explicit parental/guardian consent if you are a student booking lessons).
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            2. Account Security & Verification
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            You must provide accurate information during account registration. Instructors are solely responsible for verifying the accuracy of pupil contact details and syllabus checklists. You are responsible for maintaining password confidentiality and all activities under your credentials.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            3. Free Trial & Subscription Billing
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3, color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.7 }}>
            <li><strong>Trial Period:</strong> We offer a 14-day free trial. No credit card is required to sign up. After 14 days, you must select a premium plan to continue using scheduling and notification services.</li>
            <li><strong>Auto-Renewal:</strong> Subscriptions are billed in advance on a recurring monthly or yearly cycle depending on the chosen tier. Fees are billed in GBP (£) and exclude standard UK VAT (if applicable).</li>
            <li><strong>Refund Policy:</strong> All subscription charges are non-refundable. You can cancel your subscription at any time; your premium access will remain active until the end of your current billing period.</li>
          </Box>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            4. Service Scope & Booking Relationships
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            adiLessons provides software-as-a-service (SaaS) utility features only. We do not act as a driving school, employ driving instructors, or guarantee student lessons. All lesson cancellations, fee resolutions, and training agreements remain the direct responsibility of the student and instructor.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            5. WhatsApp Dispatches & WhapiCell Regulations
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            Instructors linking WhatsApp dispatching instances are solely responsible for ensuring they possess correct consent from their pupils to send automated notification reminders under the UK GDPR. Dispatches should be configured for transactional alerts only (booking confirmation, cancellations, changes, and reminders) to avoid policy flagging. adiLessons is not responsible for accounts blocked, suspended, or flagged by WhatsApp.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            6. Limitation of Liability & SLA
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
            While we strive for maximum uptime, the platform is provided on an "as-is" basis. To the maximum extent permitted by law, in no event shall adiLessons' liability exceed the total amount paid by the customer to adiLessons during the 12-month period preceding the claim. We are not liable for delayed SMS/WhatsApp deliveries, network outages, or lost diaries.
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2, color: '#1a237e' }}>
            7. Governing Law & Jurisdiction
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            These Terms & Conditions are governed by and construed in accordance with the laws of England and Wales. Parties agree to the exclusive jurisdiction of the courts of England and Wales.
          </Typography>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: '#0d132a', color: '#90a4ae', textAlign: 'center', borderTop: '1px solid #1c2a4a' }}>
        <Typography variant="caption">
          © {new Date().getFullYear()} adiLessons. All rights reserved. Designed in the UK.
        </Typography>
      </Box>
    </Box>
  );
};

export default TermsPage;
